/**
 * LoRa Bridge
 *
 * Provides a unified interface for Meshtastic and MeshCore backends.
 * Manages connection, message routing, and backend selection.
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

import type {
  LoRaBackend,
  LoRaConnectionStatus,
  LoRaDevice,
  LoRaMessage,
  LoRaEventHandlers,
  LoRaConfig,
  LoRaChannelMapping,
} from './types'
import { DEFAULT_LORA_CONFIG } from './types'
import {
  generateLoRaMessageId,
  encodeForMeshtastic,
  decodeFromMeshtastic,
  validateLoRaMessage,
} from './message-adapter'

// Import Meshtastic functions
import * as Meshtastic from '../meshtastic'

// MeshCore: the real companion protocol, one paced radio session
import { meshcoreSession, isMeshCoreBleAvailable, type RadioState } from '../meshcore'

// ============= Region Display Names =============

/**
 * Maps RegionCode string values to human-readable display names.
 * Used for UI display while maintaining type safety with string region codes.
 */
const REGION_DISPLAY_NAMES: Record<string, string> = {
  // Meshtastic region codes
  UNSET: 'Unset',
  US: 'United States (915 MHz)',
  EU_433: 'Europe 433 MHz',
  EU_868: 'Europe 868 MHz',
  CN: 'China (470 MHz)',
  JP: 'Japan (920 MHz)',
  ANZ: 'Australia/New Zealand (915 MHz)',
  KR: 'Korea (920 MHz)',
  TW: 'Taiwan (920 MHz)',
  RU: 'Russia (868 MHz)',
  IN: 'India (865 MHz)',
  NZ_865: 'New Zealand 865 MHz',
  TH: 'Thailand (923 MHz)',
  LORA_24: '2.4 GHz',
  UA_433: 'Ukraine 433 MHz',
  UA_868: 'Ukraine 868 MHz',
  MY_433: 'Malaysia 433 MHz',
  MY_919: 'Malaysia 919 MHz',
  SG_923: 'Singapore 923 MHz',
  // MeshCore region codes
  US915: 'United States (915 MHz)',
  EU868: 'Europe 868 MHz',
  AU915: 'Australia (915 MHz)',
  AS923: 'Asia (923 MHz)',
  KR920: 'Korea (920 MHz)',
  IN865: 'India (865 MHz)',
  JP920: 'Japan (920 MHz)',
}

// ============= State =============

let activeBackend: LoRaBackend | null = null
let config: LoRaConfig = { ...DEFAULT_LORA_CONFIG }
let eventHandlers: LoRaEventHandlers = {}
let heartbeatInterval: ReturnType<typeof setInterval> | null = null

// ============= Public API =============

/**
 * Check if any LoRa backend is available (Web Bluetooth)
 */
export function isLoRaAvailable(): boolean {
  return Meshtastic.isMeshtasticBLEAvailable() || isMeshCoreBleAvailable()
}

/**
 * Get current active backend
 */
export function getActiveBackend(): LoRaBackend | null {
  return activeBackend
}

/**
 * Get current connection status
 */
export function getLoRaStatus(): LoRaConnectionStatus {
  if (!activeBackend) return 'disconnected'

  if (activeBackend === 'meshtastic') {
    return mapMeshtasticStatus(Meshtastic.getMeshtasticStatus())
  } else {
    return mapMeshCoreStatus(meshcoreSession.snapshot)
  }
}

/**
 * Get current device info
 */
export function getLoRaDevice(): LoRaDevice | null {
  if (!activeBackend) return null

  if (activeBackend === 'meshtastic') {
    const device = Meshtastic.getMeshtasticDevice()
    if (!device) return null
    return mapMeshtasticDevice(device)
  } else {
    return mapMeshCoreDevice(meshcoreSession.snapshot)
  }
}

/**
 * Set LoRa configuration
 */
export function setLoRaConfig(newConfig: Partial<LoRaConfig>): void {
  config = { ...config, ...newConfig }
}

/**
 * Set event handlers
 */
export function setLoRaEventHandlers(handlers: LoRaEventHandlers): void {
  eventHandlers = handlers
}

/**
 * Connect to a LoRa device
 * If backend is not specified, will scan for any available device
 */
export async function connectLoRa(backend?: LoRaBackend): Promise<LoRaDevice> {
  // Disconnect any existing connection
  if (activeBackend) {
    disconnectLoRa()
  }

  const targetBackend = backend || config.preferredBackend

  if (targetBackend === 'meshtastic' || (!targetBackend && Meshtastic.isMeshtasticBLEAvailable())) {
    return connectMeshtastic()
  } else if (targetBackend === 'meshcore' || (!targetBackend && isMeshCoreBleAvailable())) {
    return connectMeshCoreDevice()
  } else {
    throw new Error('No LoRa backend available')
  }
}

/**
 * Disconnect from current device
 */
export function disconnectLoRa(): void {
  stopHeartbeat()

  if (activeBackend === 'meshtastic') {
    Meshtastic.disconnectMeshtastic()
  } else if (activeBackend === 'meshcore') {
    stopMeshCoreState?.()
    stopMeshCoreState = null
    void meshcoreSession.disconnect()
  }

  const previousBackend = activeBackend
  activeBackend = null
  if (previousBackend) {
    eventHandlers.onStatusChange?.('disconnected', previousBackend)
  }
}

/**
 * Send a message via LoRa
 */
export async function sendLoRaMessage(
  message: LoRaMessage,
  channelMapping?: LoRaChannelMapping
): Promise<void> {
  if (!activeBackend) {
    throw new Error('No LoRa connection')
  }

  if (!validateLoRaMessage(message)) {
    throw new Error('Invalid message')
  }

  // Pong messages are responses only - not sent directly
  if (message.type === 'pong') {
    throw new Error('Pong messages cannot be sent directly')
  }

  const outgoingType = message.type as 'text' | 'poi' | 'state_sync' | 'ping'

  if (activeBackend === 'meshtastic') {
    // encodeForMeshtastic validates the message format
    encodeForMeshtastic(message)
    const packet = Meshtastic.encodeCrowdTalkieMessage(
      {
        roomId: message.roomId,
        type: outgoingType,
        payload: message.payload,
        signature: message.signature,
        senderId: message.senderId,
        timestamp: message.timestamp,
      },
      Meshtastic.BROADCAST_ADDR,
      channelMapping?.meshtasticChannelIndex ?? 0
    )
    await Meshtastic.sendToRadio(packet)
  } else {
    // MeshCore carries rooms as readable channel text, paced for shared
    // airtime (see useLoRaRoomBridge). State sync and pings never go on air.
    if (outgoingType !== 'text') {
      throw new Error(`MeshCore does not carry ${outgoingType} messages over the air`)
    }
    await meshcoreSession.sendRoomText(message.roomId, message.payload)
  }
}

/**
 * Send a text message (convenience method)
 */
export async function sendTextMessage(text: string, roomId: string): Promise<void> {
  const message: LoRaMessage = {
    id: generateLoRaMessageId(),
    type: 'text',
    roomId,
    payload: text,
    timestamp: Date.now(),
    backend: activeBackend!,
  }

  await sendLoRaMessage(message)
}

// ============= Meshtastic Connection =============

async function connectMeshtastic(): Promise<LoRaDevice> {
  // Set up Meshtastic event handlers
  Meshtastic.setMeshtasticEventHandlers({
    onStatusChange: (status) => {
      const mappedStatus = mapMeshtasticStatus(status)
      eventHandlers.onStatusChange?.(mappedStatus, 'meshtastic')
    },
    onPacketReceived: (packet) => {
      handleMeshtasticPacket(packet)
    },
    onMyInfoReceived: (info) => {
      if (config.debug) {
        console.log('[LoRa] Meshtastic info:', info)
      }
    },
    onError: (error) => {
      eventHandlers.onError?.(error, 'meshtastic')
    },
    onDisconnect: () => {
      eventHandlers.onDisconnect?.('meshtastic')
      if (config.autoReconnect) {
        // Auto-reconnect handled by Meshtastic module
      }
    },
  })

  const device = await Meshtastic.connectToMeshtastic()
  activeBackend = 'meshtastic'

  // Start heartbeat
  startHeartbeat()

  const loraDevice = mapMeshtasticDevice(device)
  eventHandlers.onDeviceInfo?.(loraDevice)

  return loraDevice
}

function handleMeshtasticPacket(packet: Meshtastic.MeshPacket): void {
  // Only process decoded packets with PRIVATE_APP portnum
  if (!packet.decoded || packet.decoded.portnum !== 'PRIVATE_APP') {
    return
  }

  const message = decodeFromMeshtastic(packet.decoded.payload)
  if (message && validateLoRaMessage(message)) {
    eventHandlers.onMessageReceived?.(message)
  }
}

// ============= MeshCore Connection =============

let stopMeshCoreState: (() => void) | null = null

async function connectMeshCoreDevice(): Promise<LoRaDevice> {
  stopMeshCoreState?.()
  let previous = meshcoreSession.snapshot.status
  stopMeshCoreState = meshcoreSession.onState((state) => {
    if (state.status === previous) return
    previous = state.status
    eventHandlers.onStatusChange?.(mapMeshCoreStatus(state), 'meshcore')
    if (state.status === 'disconnected' && state.error) {
      eventHandlers.onDisconnect?.('meshcore')
    }
    if (state.status === 'error' && state.error) {
      eventHandlers.onError?.(new Error(state.error), 'meshcore')
    }
  })

  await meshcoreSession.connect()
  activeBackend = 'meshcore'

  const loraDevice = mapMeshCoreDevice(meshcoreSession.snapshot)!
  eventHandlers.onDeviceInfo?.(loraDevice)
  return loraDevice
}

// ============= Heartbeat =============

function startHeartbeat(): void {
  stopHeartbeat()

  heartbeatInterval = setInterval(async () => {
    try {
      if (activeBackend === 'meshtastic') {
        await Meshtastic.sendHeartbeat()
      } else if (activeBackend === 'meshcore') {
        // MeshCore doesn't require explicit heartbeat
        // but we can send a ping to keep connection alive
      }
    } catch (error) {
      if (config.debug) {
        console.error('[LoRa] Heartbeat failed:', error)
      }
    }
  }, config.heartbeatIntervalMs)
}

function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval)
    heartbeatInterval = null
  }
}

// ============= Status Mapping =============

function mapMeshtasticStatus(status: Meshtastic.MeshtasticConnectionStatus): LoRaConnectionStatus {
  return status as LoRaConnectionStatus
}

function mapMeshCoreStatus(state: RadioState): LoRaConnectionStatus {
  return state.status
}

// ============= Device Mapping =============

function mapMeshtasticDevice(device: Meshtastic.MeshtasticDevice): LoRaDevice {
  const regionCode = device.config?.region
  const regionDisplay = regionCode ? (REGION_DISPLAY_NAMES[regionCode] ?? regionCode) : 'unknown'

  return {
    id: device.id,
    name: device.name,
    backend: 'meshtastic',
    firmwareVersion: device.myInfo?.firmwareVersion ?? 'unknown',
    batteryLevel: device.nodeInfo?.deviceMetrics?.batteryLevel ?? 0,
    hasGps: device.myInfo?.hasGps ?? false,
    region: regionDisplay,
    lastSeen: device.lastSeen,
    isConnected: device.server?.connected ?? false,
  }
}

function mapMeshCoreDevice(state: RadioState): LoRaDevice | null {
  const info = state.selfInfo
  if (!info) return null
  const r = info.radio
  return {
    id: info.publicKey.slice(0, 16),
    name: info.name,
    backend: 'meshcore',
    firmwareVersion: state.deviceInfo?.version ?? 'unknown',
    batteryLevel: 0,
    hasGps: false,
    // What the radio itself reports, never what we asked for.
    region: `${r.freqMHz} MHz / ${r.bwKHz} kHz / SF${r.sf} / CR${r.cr}`,
    lastSeen: new Date(),
    isConnected: state.status === 'ready',
  }
}

// ============= Utility Functions =============

/**
 * Get available backends
 */
export function getAvailableBackends(): LoRaBackend[] {
  const backends: LoRaBackend[] = []
  if (Meshtastic.isMeshtasticBLEAvailable()) {
    backends.push('meshtastic')
  }
  if (isMeshCoreBleAvailable()) {
    backends.push('meshcore')
  }
  return backends
}

/**
 * Get backend display name
 */
export function getBackendDisplayName(backend: LoRaBackend): string {
  return backend === 'meshtastic' ? 'Meshtastic' : 'MeshCore'
}

/**
 * Check if currently connected
 */
export function isLoRaConnected(): boolean {
  const status = getLoRaStatus()
  return status === 'connected' || status === 'ready'
}
